import { Task } from "@Core/models/codegen/area.models";
import _ from "lodash";
import { TimerType } from "../enums/timer-type.enum";
import { PriorityType } from "@Conf/Timers/prioritization/prioritization.component";
import { PriorityIndexMap, TimerImminentDates, TimeZone } from "@Work/Services/task-timer.service";

export interface TaskTimer {
    WorkflowId: string;
    TaskId: string;
    TaskName: string;
    Type: TimerType;
    DueOn: Date;
    Imminence?: string;
    Priority?: number;
    ImminentDate?: Date;
}

export class TaskUtils {
    /**
     *   Takes a Task object and returns the most imminent TaskTimer object
     *   @param task A Task object
    */
    /**
     * Returns the most‑imminent TaskTimer for a single task.
     * – If `prioritization` & `businessLocation` are supplied, uses advanced logic
     *   (Late < Imminent < Pending + configured type order).
     * – If omitted, falls back to simple "earliest DueOn" behaviour (legacy).
     */
    public static getMostImminentTimerOnTask(task: Task, priorityMap: PriorityIndexMap = null): TaskTimer {
        if (!task)
            return null;

        if (task.CompletionStatus === "Done" || task.CompletionStatus === "Waived")
            return null;

        const timers = this.buildTaskTimers(task, priorityMap);

        if (!timers?.length)
            return null;

        const sorted = this.sortTaskTimers(timers, priorityMap);
        return sorted[0] ?? null;
    }

    /**
     * Returns the most‑imminent TaskTimer across a list of tasks.
     * Mirrors single‑task logic for consistency.
     */
    public static getMostImminentTimerInTaskList(tasks: Task[], priorityMap: PriorityIndexMap = null): TaskTimer {
        const timers = tasks
            .map(t => this.getMostImminentTimerOnTask(t, priorityMap))
            .filter(Boolean) as TaskTimer[];

        if (!timers.length)
            return null;

        const sorted = this.sortTaskTimers(timers, priorityMap);
        return sorted[0] ?? null;
    }

    public static buildTaskTimers(task: Task, priorityMap: PriorityIndexMap = null): TaskTimer[] {
        if (!task)
            return [];

        const manualOverride = task?.ManualDueDateImminentDate
            ? new Date(task.ManualDueDateImminentDate)
            : null;

        return TASK_TIMER_PROPERTIES
            .filter(({ dueDateProp }) => task[dueDateProp] != null)
            .map(({ dueDateProp, imminentDateProp, type }) => {
                const timerDueDate = new Date(task[dueDateProp]);

                const imminentDate = type == TimerType.Manual && manualOverride != null
                    ? manualOverride
                    : new Date(task[imminentDateProp] ?? null);

                const taskTimer: TaskTimer = {
                    WorkflowId: task.WorkflowId,
                    TaskId: task.Id,
                    TaskName: task.Name,
                    Type: type,
                    DueOn: timerDueDate,
                    ImminentDate: null,
                    Imminence: null,
                    Priority: null
                };

                // Fallback to legacy behaviour if no prioritization or imminent date
                if (!priorityMap || !imminentDate)
                    return taskTimer;

                const imminence = this.getImminence(timerDueDate, imminentDate);
                taskTimer.Imminence = imminence;
                taskTimer.Priority = this.getPriority(imminence, type, priorityMap);
                taskTimer.ImminentDate = imminentDate;

                return taskTimer;
            });
    }

    public static sortTaskTimers(timers: TaskTimer[], priorityMap: PriorityIndexMap = null): TaskTimer[] {
        if (!timers?.length)
            return [];

        const hasPriority = priorityMap && Object.keys(priorityMap).length > 0;

        return hasPriority
            ? _(timers).sortBy(["Priority", "DueOn"]).value()
            : _(timers).sortBy(["DueOn"]).value();
    }

    private static getImminence(dueOn: Date, imminentDate: Date): string {
        if (!dueOn || !imminentDate)
            return null;

        const now = new Date();

        if (now >= dueOn)
            return PriorityType[PriorityType.Late];
        if (now >= imminentDate)
            return PriorityType[PriorityType.Imminent];

        return PriorityType[PriorityType.Pending];
    }

    private static getPriority(imminence: string, timerType: TimerType, priorityMap: PriorityIndexMap): number {
        if (!imminence)
            return null;

        let dateType: 'DueDate' | 'Deadline' | 'ServiceLevel';

        if (timerType === TimerType.Manual) {
            dateType = 'DueDate';
        } else if (timerType === TimerType.Deadline) {
            dateType = 'Deadline';
        } else {
            dateType = 'ServiceLevel';
        }

        const prioritizationType = imminence + dateType;
        const priority = priorityMap[prioritizationType];

        return priority;
    }
}

export interface TaskTimerProperty {
    type: TimerType;
    dueDateProp: keyof Task;
    imminentDateProp: keyof Task;
}

export const TASK_TIMER_PROPERTIES: TaskTimerProperty[] = [
    {
        type: TimerType.Manual,
        dueDateProp: 'ManualDueDate',
        imminentDateProp: 'ManualDueDateImminentDate'
    },
    {
        type: TimerType.Deadline,
        dueDateProp: 'Deadline',
        imminentDateProp: 'DeadlineImminentDate',
    },
    {
        type: TimerType.SLA,
        dueDateProp: 'SLADueOn',
        imminentDateProp: 'SLAImminentDate'
    }
];